












































































































import { Component, Vue, Prop, Watch, PropSync, Ref } from 'vue-property-decorator';
import { IPopupButton } from './Popup';
import { boundingToFitViewport } from '@/utils/viewport';
import { generateUID } from '@/utils/utils';

@Component({
  i18n: {
    messages: {
      en: {
        cancel: 'Cancel',
        confirm: 'Confirm',
      },
      nb: {
        cancel: 'Avbryt',
        confirm: 'Bekreft',
      },
    },
  },
})
export default class SPopup extends Vue {
  @PropSync('open', { default: false, type: Boolean }) public openSync!: boolean;
  @Prop({ default: false, type: Boolean }) public dimmer!: boolean;
  @Prop({ default: true, type: Boolean }) public withCloseButton!: boolean;
  @Prop({ default: '', type: String }) public message!: string;
  @Prop({ default: '', type: String }) public title!: string;
  @Prop({ default: '', type: String }) public confirmText!: string;
  @Prop({ default: '', type: String }) public closeText!: string;
  @Prop({ default: false, type: Boolean }) public pinned!: boolean;
  @Prop({ default: true, type: Boolean }) public movable!: boolean;
  @Prop({ default: true, type: Boolean }) public pinnable!: boolean;
  @Prop({ default: true, type: Boolean }) public resizable!: boolean;
  @Prop({ default: true, type: Boolean }) public closeOnAction!: boolean;
  @Prop(Function) public onConfirm?: () => void;
  @Prop(Function) public onCancel?: () => void;
  @Prop({ default: () => [], type: Array }) public buttons!: IPopupButton[];
  @Prop({ default: null, type: Object }) public parentPopup!: SPopup | null;

  @Ref() public readonly modalRef!: HTMLElement;
  @Ref() public readonly handleRef!: HTMLElement;
  @Ref() public readonly popupRef!: HTMLElement;

  public mouseTarget?: HTMLElement;
  public dragging = false;
  public moved = false;
  public customPinned = this.pinned;

  public top = '50vh';
  public left = '50vw';
  public observer!: MutationObserver;
  public observing = false;

  private uid = generateUID();

  public created() {
    this.observer = new MutationObserver(this.setInitPosition);
  }

  public mounted() {
    this.setInitPosition();
    this.makeActive();
  }

  get confirmTextCalculated() {
    return this.confirmText || this.$t('confirm');
  }

  get closeTextCalculated() {
    return this.closeText || this.$t('cancel');
  }

  @Watch('openSync')
  public changeOpen() {
    this.$nextTick(() => {
      if (this.observing) {
        this.observing = false;
      } else if (this.modalRef) {
        this.observer.observe(
          (this.modalRef.querySelector('main') as HTMLElement),
          { attributes: true, childList: true, characterData: true, subtree: true },
        );
        this.observing = true;
      }
      this.setActivePosition();
      this.makeActive();
    });
  }

  public setInitPosition() {
    if (this.openSync && !this.moved) {
      this.$nextTick(() => {
        if (!this.modalRef) return;
        const height = this.modalRef.offsetHeight;
        const width = this.modalRef.offsetWidth;
        let top = 0;
        let left = 0;
        if (this.customPinned) {
          top = window.pageYOffset || document.documentElement.scrollTop;
          left = window.pageXOffset || document.documentElement.scrollLeft;
        }
        this.top = `calc(50vh - ${height / 2 - top}px)`;
        this.left = `calc(50vw - ${width / 2 - left}px)`;
      });
    }
  }

  public togglePopup() {
    this.openSync = !this.openSync;
  }

  public onDragStart(event: DragEvent) {
    if (!this.movable) return;
    if (
      this.mouseTarget &&
      this.handleRef.contains(this.mouseTarget)
    ) {
      this.setActive();
      this.dragging = true;
      (event.dataTransfer as DataTransfer).effectAllowed = 'move';
      (event.dataTransfer as DataTransfer).dropEffect = 'move';
      const target = (event.target as Element);
      const style = window.getComputedStyle(target, null);
      const left = parseInt(style.getPropertyValue('left'), 10) - event.clientX;
      const top = parseInt(style.getPropertyValue('top'), 10) - event.clientY;
      (event.dataTransfer as DataTransfer).setData(
        'text/plain',
        `${left},${top}`,
      );
    } else {
      event.preventDefault();
      return false;
    }
  }

  public onDrop(event: DragEvent) {
    if (!this.movable && !this.dragging) return;
    let left = event.clientX;
    let top = event.clientY;
    this.moved = true;
    this.dragging = false;
    if (event.dataTransfer) {
      const [ leftExtra, topExtra ] = event.dataTransfer
        .getData('text/plain')
        .split(',')
        .map(style => parseInt(style, 10));
      left += leftExtra;
      top += topExtra;
    }

    this.left = `${left}px`;
    this.top = `${top}px`;
    event.preventDefault();
    return false;
  }

  public onDragOver(event: DragEvent) {
    if (!this.movable) return;
    (event.dataTransfer as DataTransfer).effectAllowed = 'move';
    (event.dataTransfer as DataTransfer).dropEffect = 'move';
    return false;
  }

  public onDragEnd() {
    if (!this.movable && !this.dragging) return;
    this.dragging = false;
    this.makeActive();
  }

  public onMouseDown(event: MouseEvent) {
    if (!this.movable) return;
    this.mouseTarget = (event.target as HTMLElement);
  }

  public togglePin() {
    const rect = this.modalRef.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = rect.top;
    let left = rect.left;
    if (!this.customPinned) {
      top += scrollTop;
      left += scrollLeft;
    }
    this.top = `${top}px`;
    this.left = `${left}px`;
    this.customPinned = !this.customPinned;
  }

  private setActivePosition() {
    const activePopup = this.popupRef?.parentElement?.querySelector('.active');

    if (this.parentPopup?.modalRef && this.modalRef) {
      const rect = this.parentPopup.modalRef.getBoundingClientRect();

      this.top = `${rect.top + rect.height / 2 - this.modalRef.getBoundingClientRect().height / 2}px`;
      this.left = `${rect.left + rect.width + 20}px`;

      this.moved = true;
    } else if (activePopup) {
      const activePopupSection = activePopup.getElementsByTagName('section')[0];
      if (activePopupSection) {
        this.top = `calc(${activePopupSection.style.top} + 50px)`;
        this.left = `calc(${activePopupSection.style.left} + 50px)`;
      }
      this.moved = true;
    } else {
      this.setInitPosition();
    }
  }

  public setActive() {
    if (this?.popupRef?.parentElement) {
      this.makeAllPopupInactive();
      this.popupRef.classList.add('active');
    }
  }

  public makeAllPopupInactive() {
    const elements = this.popupRef.parentElement!.querySelectorAll('.active');
    elements.forEach(elem => elem.classList.remove('active'));
  }

  public wrapToggleCallback(callback?: Function) {
    if (callback) callback();
    if (this.closeOnAction) this.togglePopup();
  }

  public onWindowResize() {
    if (!this.moved || this.customPinned) return;

    const { top, left } = boundingToFitViewport(this.modalRef);
    this.top = `${top}px`;
    this.left = `${left}px`;
  }

  private makeActive() {
    if (this.openSync) this.$nextTick(this.setActive);
  }
}
